import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ArrowLeft, ArrowRight, AtSign, Eye, EyeOff, Lock, MailX, ShieldCheck, User, UserRound } from 'lucide-react'
import AuthField from '../auth/AuthField'
import AuthHead from '../auth/AuthHead'
import AuthLayout from '../auth/AuthLayout'
import Button from '../../components/Button'
import PageLoader from '../../components/PageLoader'
import ResultDialog from '../../components/ResultDialog'
import { useResource } from '../../lib/useResource'
import { getSignupPage, signup } from './signupService'
import { strings as S } from './strings'
/* The same surface `/activate/expired` wears — see the refusal branch below. */
import './ActivatePage.css'

const LOGO_SRC = '/images/Rawam-logo-reporting.png'

const EMAIL_RULE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export default function SignupPage() {
  const navigate = useNavigate()

  /* Invitation emails link to /Signup?id={guid}. The id decides whether the email field
     is the visitor's to choose or the invitation's to display. */
  const [params] = useSearchParams()
  const invitationId = params.get('id') ?? params.get('Id')

  const { data: page, loading } = useResource(
    (signal) => getSignupPage(invitationId, signal),
    [invitationId],
  )
  const invitation = page?.invitation ?? null

  const [form, setForm] = useState({
    nameAr: '',
    nameEn: '',
    email: '',
    username: '',
    password: '',
    cPassword: '',
  })
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [errors, setErrors] = useState({})
  const [submitting, setSubmitting] = useState(false)
  const [result, setResult] = useState(null)

  const set = (key) => (value) => setForm((prev) => ({ ...prev, [key]: value }))

  // The invitation fixes the address; it arrives after the first render.
  useEffect(() => {
    if (invitation?.email) setForm((prev) => ({ ...prev, email: invitation.email }))
  }, [invitation?.email])

  /** Mirrors the FormValidation rules in Views/Signup/js.cshtml. */
  function validate() {
    const next = {}
    const { nameAr, nameEn, email, username, password, cPassword } = form

    for (const [key, value] of [
      ['nameAr', nameAr],
      ['nameEn', nameEn],
    ]) {
      if (value.trim() === '') next[key] = S.messages.required
      else if (value.trim().length < 3 || value.trim().length > 200) next[key] = S.messages.length
    }

    if (email.trim() === '') next.email = S.messages.emailRequired
    else if (!EMAIL_RULE.test(email.trim())) next.email = S.messages.emailInvalid

    if (username.trim() === '') next.username = S.messages.required
    if (password === '') next.password = S.messages.required

    if (cPassword === '') next.cPassword = S.messages.required
    else if (cPassword !== password) next.cPassword = S.messages.passwordMismatch

    setErrors(next)
    return Object.keys(next).length === 0
  }

  async function handleSubmit(e) {
    e.preventDefault()
    if (!validate()) return

    setSubmitting(true)
    const data = await signup({ ...form, invitationId: invitation?.id ?? invitationId })
    setSubmitting(false)

    setResult({ result: data.Result, message: data.Message, nextStep: data.NextStep })
  }

  /** Legacy routes by nextStep once the success dialog is dismissed. */
  function handleResultClose() {
    const next = result?.nextStep
    setResult(null)

    if (next === 'User') navigate('/activate/waiting-user')
    else if (next === 'Admin') navigate('/activate/waiting-admin')
    else if (result?.result) navigate('/login')
  }

  if (loading) {
    return (
      <AuthLayout>
        <div className="auth__card">
          <PageLoader />
        </div>
      </AuthLayout>
    )
  }

  /* Registration can be closed for three reasons the server knows and this app does not
     — the invitation expired, public signup is off, or the licence has no room. The
     Razor handler rendered the shared error view for exactly this; here it is the same
     message on the same surface as every other auth state, so there is one less layout
     in the product. The message is the server's, displayed as received (W6). */
  if (page && !page.canSignup) {
    return (
      <AuthLayout>
        <div className="auth__card">
          {/* THE SAME SHAPE AS `/activate/expired`, which is the same news: a link that no
              longer opens anything. It was a red alert block the width of the card with the
              way out tucked under one edge — the loudest thing on the page was the box, not
              what to do next. A mark, the server's sentence, and one action, centred. */}
          <AuthHead
            title={S.closedTitle}
            subtitle={page.appTitle || S.appTitle}
            mark={
              <span className="activate__badge activate__badge--closed" aria-hidden="true">
                <MailX size={22} />
              </span>
            }
          />
          <div className="auth__body activate">
            <p className="activate__message" role="alert">
              <bdi>{page.errorMessage}</bdi>
            </p>
            <Button variant="soft" onClick={() => navigate('/login')}>
              <ArrowRight className="ltr-flip" size={16} strokeWidth={1.75} aria-hidden="true" />
              {S.backToLogin}
            </Button>
          </div>
        </div>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout>
      <form className="auth__card auth__card--wide" onSubmit={handleSubmit} noValidate>
        <AuthHead
          title={S.title}
          subtitle={S.appTitle}
          mark={
            <span className="auth__logo auth__logo--plate">
              <img src={LOGO_SRC} alt={S.logoAlt} />
            </span>
          }
        />

        <div className="auth__pair">
          <AuthField
              id="NameAr"
              icon={UserRound}
              label={S.nameAr}
              value={form.nameAr}
              onChange={set('nameAr')}
              autoComplete="off"
              error={errors.nameAr}
            />

          <AuthField
              id="NameEn"
              icon={UserRound}
              label={S.nameEn}
              value={form.nameEn}
              onChange={set('nameEn')}
              autoComplete="off"
              error={errors.nameEn}
            />
        </div>

        <div className="auth__pair">
          <AuthField
              id="Email"
              icon={AtSign}
              label={S.email}
              value={form.email}
              onChange={set('email')}
              readOnly={Boolean(invitation)}
              autoComplete="off"
              error={errors.email}
            />

          <AuthField
              id="Username"
              icon={User}
              label={S.username}
              value={form.username}
              onChange={set('username')}
              autoComplete="off"
              error={errors.username}
            />
        </div>

        <div className="auth__pair">
          <AuthField
            id="Password"
              icon={Lock}
              type={showPassword ? 'text' : 'password'}
              label={S.password}
              value={form.password}
              onChange={set('password')}
              autoComplete="new-password"
              trailing={
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={S.password}
                >
                  {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                </button>
              }
            error={errors.password}
          />

          <AuthField
            id="CPassword"
              icon={ShieldCheck}
              type={showConfirm ? 'text' : 'password'}
              label={S.confirmPassword}
              value={form.cPassword}
              onChange={set('cPassword')}
              autoComplete="new-password"
              trailing={
                <button
                  type="button"
                  onClick={() => setShowConfirm((v) => !v)}
                  aria-label={S.confirmPassword}
                >
                  {showConfirm ? <EyeOff size={17} /> : <Eye size={17} />}
                </button>
              }
            error={errors.cPassword}
          />
        </div>

        <Button type="submit" variant="primary" className="auth__submit" disabled={submitting}>
          {submitting ? S.submitting : S.title}
          {!submitting && <ArrowLeft className="ltr-flip" size={17} aria-hidden="true" />}
        </Button>

        <footer className="auth__foot">
          <a className="auth__footLink" href="/login">
            {S.alreadyRegistered}
          </a>
        </footer>
      </form>

      <ResultDialog result={result} closeLabel={S.close} onClose={handleResultClose} />
    </AuthLayout>
  )
}
